'use client';

import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import type { Appearance, StripeElementsOptions } from '@stripe/stripe-js';

/**
 * Stripe Elements in the hub's own colours, in the page's ACTUAL theme.
 *
 * Before, the add-ons sheet and the photo purchase each guessed their theme
 * their own way: one from the `.dark` class with hard-coded hex, the other by
 * parsing themeColors.primary as hex, which in the hub is `var(--fvd-ink)`,
 * so it never knew the page's theme and passed Stripe a CSS variable it
 * cannot read as the accent. Stripe was left to its own defaults, which is
 * how a dark card block came to sit inside a light page. globals.css also puts
 * `color-scheme: dark` on :root for everyone, which the frame around the
 * iframes inherited whatever the theme.
 *
 * So the colours are read from the v8 tokens as they resolve at the element
 * the form sits in (theme, organiser accent, Hyrox skin and all), passed to
 * Stripe as concrete values (it cannot read CSS variables), and the frame's
 * color-scheme is set to match whatever that resolved to. Light or dark is
 * decided by the canvas colour itself, not prefers-color-scheme.
 */

export interface StripeTokens {
  canvas: string;
  ink: string;
  inkSecondary: string;
  inkMuted: string;
  accent: string;
  accentTint: string;
  danger: string;
  border: string;
  fontFamily: string;
}

/** Parse rgb()/rgba() or #hex into [r, g, b] (0 to 255). */
export function parseColour(value: string): [number, number, number] | null {
  const v = value.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgb = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i.exec(v);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

/** True when a colour is dark enough to need light text (relative luminance). */
export function isDarkColour(value: string): boolean {
  const c = parseColour(value);
  if (!c) return false;
  const [r, g, b] = c.map((x) => {
    const s = x / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.25;
}

/** Inputs as the rest of the hub draws them (FvdInput): hairline, 8px, accent on focus. */
export function buildStripeAppearance(t: StripeTokens, opts: { isMobile?: boolean } = {}): Appearance {
  const dark = isDarkColour(t.canvas);
  const control = { border: `1px solid ${t.border}`, boxShadow: 'none', backgroundColor: t.canvas };
  return {
    theme: dark ? 'night' : 'stripe',
    variables: {
      colorPrimary: t.accent,
      colorBackground: t.canvas,
      colorText: t.ink,
      colorTextSecondary: t.inkSecondary,
      colorTextPlaceholder: t.inkMuted,
      colorIcon: t.inkMuted,
      colorDanger: t.danger,
      fontFamily: t.fontFamily,
      // 16px on a phone so iOS does not zoom into the card field.
      fontSizeBase: opts.isMobile ? '16px' : '14px',
      borderRadius: '8px',
      spacingUnit: '4px',
    },
    rules: {
      '.Input': { ...control, padding: opts.isMobile ? '12px' : '10px 12px' },
      '.Input:focus': { borderColor: t.accent, boxShadow: `0 0 0 3px ${t.accentTint}` },
      '.Input--invalid': { borderColor: t.danger, boxShadow: 'none' },
      '.Label': { color: t.inkSecondary, fontWeight: '600', fontSize: '12px' },
      '.Tab': control,
      '.Tab:hover': { color: t.ink },
      '.Tab--selected': { borderColor: t.accent, boxShadow: 'none', backgroundColor: t.accentTint },
      '.AccordionItem': control,
      '.Error': { color: t.danger },
    },
  };
}

/** Geist for the card fields, as the page around them. Stripe's iframe cannot see the page's fonts. */
export const HUB_STRIPE_FONTS: StripeElementsOptions['fonts'] = [
  { cssSrc: 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600&display=swap' },
];

const TOKEN_VARS: Record<Exclude<keyof StripeTokens, 'fontFamily'>, string> = {
  canvas: '--fvd-canvas',
  ink: '--fvd-ink',
  inkSecondary: '--fvd-ink-secondary',
  inkMuted: '--fvd-ink-muted',
  // The bright accent in dark (as .fvd-field:focus uses), so the selected
  // method and links stay readable; the solid fill is too dim on a dark field.
  accent: '--fvd-teal',
  accentTint: '--fvd-teal-tint',
  danger: '--fvd-danger',
  border: '--fvd-border-control',
};

/**
 * Resolve the tokens at `el` to concrete rgb() values. A probe element asks
 * the browser, so whatever a token is written as (hex, rgba, another var)
 * comes back as something Stripe can parse.
 */
export function readStripeTokens(el: HTMLElement): StripeTokens {
  const probe = document.createElement('span');
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  el.appendChild(probe);
  const out = {} as StripeTokens;
  for (const [key, cssVar] of Object.entries(TOKEN_VARS) as Array<[keyof typeof TOKEN_VARS, string]>) {
    probe.style.color = `var(${cssVar})`;
    out[key] = getComputedStyle(probe).color;
  }
  probe.style.fontFamily = 'var(--fvd-font-body)';
  const family = getComputedStyle(probe).fontFamily;
  el.removeChild(probe);
  // next/font names the face "Geist" plus a generated fallback; the iframe
  // gets the Google Fonts Geist above, then the system stack.
  out.fontFamily = `${family.replace(/"?Geist Fallback"?,?\s*/g, '')}, system-ui, -apple-system, sans-serif`.replace(/^,\s*/, '');
  return out;
}

/**
 * The appearance for the Elements inside the element given `frameRef`, kept
 * in step with the page: it re-reads when the theme class, brand skin or
 * inline tokens on <html> change. `null` until that element exists, so callers
 * render a skeleton rather than a form in the wrong colours. `frameStyle` goes
 * on the same element so the iframes are not painted in the wrong colour
 * scheme.
 *
 * A callback ref rather than a ref object, because the element often appears
 * later than the component that owns it (FvdWindow portals its body in only
 * after its own mount), and an effect keyed on a ref object never hears that.
 */
export function useHubStripeAppearance(isMobile: boolean) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [state, setState] = useState<{ appearance: Appearance; dark: boolean } | null>(null);

  const read = useCallback(() => {
    if (!el) return;
    const tokens = readStripeTokens(el);
    const appearance = buildStripeAppearance(tokens, { isMobile });
    const dark = isDarkColour(tokens.canvas);
    setState((prev) => (prev && JSON.stringify(prev.appearance) === JSON.stringify(appearance) ? prev : { appearance, dark }));
  }, [el, isMobile]);

  useEffect(() => {
    if (!el) return;
    // A frame later, once the element's styles have resolved.
    const frame = requestAnimationFrame(read);
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style', 'data-brand-preview', 'data-theme'] });
    return () => { cancelAnimationFrame(frame); observer.disconnect(); };
  }, [el, read]);

  const frameStyle: CSSProperties = { colorScheme: state ? (state.dark ? 'dark' : 'light') : undefined };
  return { frameRef: setEl as (node: HTMLElement | null) => void, appearance: state?.appearance ?? null, dark: state?.dark ?? false, frameStyle };
}
